import {
  List,
  Pagination,
  Toast,
  ToastType,
} from '@alefeducation/ui-elements-library-core';
import React, { useState } from 'react';
import styled from 'styled-components';
import { useMutation } from '@apollo/client';
import { useParams, useHistory } from 'react-router-dom';
import useIframeAuth from '../hooks/useIframeAuth';
import { Member } from '../../apollo/queries';
import MemberColHeader from '../../components/OrganisationMembers/MemberColHeader';
import MemberRows from '../../components/OrganisationMembers/MemberRows';
import Loading from '../../components/Loading';
import { NoItemsView } from '../../components/Organisations';
import { tableOverflow } from '../../styles/Common';
import { DELETE_USER_MEMBERSHIP } from '../../apollo/mutations';
import { injectAutoHideToastOfType } from '../../utils/Toast';
import { ConfirmModal } from '../../utils/ConfirmModal';
import {
  MEMBERSHIP_SUCCESSFULLY_REMOVED,
  NO_ORG_MEMBERS_DESCRIPTION_MESSAGE,
  NO_ORG_MEMBERS_TITLE_MESSAGE,
  OOPS_SOMETHING_WENT_WRONG_MESSAGE,
  REMOVE_USER_CONFIRMATION_MESSAGE,
  REMOVING_MEMBERSHIP_MESSAGE,
  SOMETHING_WENT_WRONG_MESSAGE,
  USERS_PER_PAGE,
} from '../../utils/constants';
import { GET_USERS_V3 } from '../../apollo/queries/v3/getUsersV3.queries';
import usePagination from '../../components/Pagination/hooks/usePagination';
import PageStatus from '../../components/Pagination/PageStatus';
import isInCCLiframe from '../../utils/isInCCLiframe';
import { FEATURE_TOGGLE } from '../../utils/FeatureToggle';
import appLocalStorage from '../../utils/AppLocalStorage';

interface LocationState {
  pageNumber: number;
}

const StyledMembersContainer = styled(List)`
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 0;
  border: 0;
`;

const StyledTableBody = styled.div`
  height: 100%;
  overflow-y: auto;

  ${tableOverflow};
`;

const StyledPaginationContainer = styled.div`
  margin: ${({ theme: { space } }: any) => `${space[24]}px`};
`;

const OrganisationMembers = () => {
  const { code } = useParams<{ code: string }>();
  const history = useHistory();
  const pageNumber = history.location.state
    ? (history.location.state as LocationState).pageNumber
    : 0;

  const usersQuery = GET_USERS_V3;
  const responseDataFieldName = 'usersV3';
  const { isAuthorized, userDataAvailable, permissionsAvailable } =
    useIframeAuth({ isFromMembersPage: true });

  const {
    queryData: { loading, error, data, refetch },
    pageData: { currentPage, totalPages, pageStats },
    actions: { setCurrentPage },
  } = usePagination({
    query: usersQuery,
    responseField: responseDataFieldName,
    pageSize: USERS_PER_PAGE,
    currentPage: pageNumber + 1,
    variables: { organisation: code },
  });

  const [isOpen, setIsOpen] = useState(false);
  const [selectedMemberId, setMemberId] = useState<number | null>(null);
  const TOAST_ID = 'toast-member-list';

  const [deleteMembership, { loading: removeInProgress }] = useMutation(
    DELETE_USER_MEMBERSHIP,
    {
      onCompleted: () => {
        refetch();
        injectAutoHideToastOfType(
          TOAST_ID,
          MEMBERSHIP_SUCCESSFULLY_REMOVED,
          ToastType.PROGRESS_COMPLETE,
        );
        setMemberId(null);
      },
      onError: (e) => {
        injectAutoHideToastOfType(
          TOAST_ID,
          SOMETHING_WENT_WRONG_MESSAGE,
          ToastType.ERROR,
        );
        setMemberId(null);
      },
    },
  );

  const removeMembership = () => {
    deleteMembership({
      variables: {
        id: selectedMemberId,
        organisation: code,
      },
    });
    setIsOpen(false);
  };

  const userData = data && data[responseDataFieldName];
  const users = userData ? userData.users : [];

  const showPagination = () => !error && users.length > 0 && totalPages > 0;

  return (
    <StyledMembersContainer>
      <MemberColHeader />

      {loading && <Loading />}

      {error && <NoItemsView title={OOPS_SOMETHING_WENT_WRONG_MESSAGE} />}

      {!loading &&
        !error &&
        (!isInCCLiframe || (userDataAvailable && permissionsAvailable)) && (
          <React.Fragment>
            <StyledTableBody>
              {users.length === 0 ? (
                <NoItemsView
                  title={NO_ORG_MEMBERS_TITLE_MESSAGE}
                  description={NO_ORG_MEMBERS_DESCRIPTION_MESSAGE}
                />
              ) : (
                <MemberRows
                  hasUpdatePermission={isAuthorized('update:user')}
                  hasAssignPermission={
                    isAuthorized('assign:user') &&
                    !appLocalStorage.isViewOnlyRole()
                  }
                  members={[...users].sort((a: Member, b: Member) => {
                    const fieldName = FEATURE_TOGGLE.ENABLE_AUTH0_INTEGRATION
                      ? 'firstName'
                      : 'name';
                    const aName = a[fieldName] || '';
                    const bName = b[fieldName] || '';
                    return aName.localeCompare(bName);
                  })}
                  orgCode={code}
                  onRemove={(memberId: number) => {
                    setIsOpen(true);
                    setMemberId(memberId);
                  }}
                  pageNumber={currentPage - 1}
                />
              )}
            </StyledTableBody>
          </React.Fragment>
        )}
      {showPagination() && (
        <StyledPaginationContainer>
          <Pagination
            initialPage={currentPage}
            onPageChange={(page) => {
              setCurrentPage(page);
            }}
            pages={totalPages}
            data-testid="pagination"
          />
          <PageStatus isHidden={loading}>{pageStats}</PageStatus>
        </StyledPaginationContainer>
      )}
      <div id="edit-status-toast"></div>
      <ConfirmModal
        open={isOpen}
        heading=""
        message={REMOVE_USER_CONFIRMATION_MESSAGE}
        modalVisibility={setIsOpen}
        onYesClick={removeMembership}
      />
      <div id={TOAST_ID} />
      <Toast
        autoHide={false}
        isOpen={removeInProgress}
        content={{
          title: REMOVING_MEMBERSHIP_MESSAGE,
          description: '',
        }}
        data-testid={`toast-progress`}
        type={ToastType.PROGRESS}
      />
    </StyledMembersContainer>
  );
};

export default OrganisationMembers;
